<template>
  <div>
    <label v-if="label" class="tw-mb-1 tw-block tw-text-xs dark:tw-text-white">
      {{ label }}
    </label>
    <div class="tw-w-full tw-rounded-md tw-border tw-bg-white hover:tw-border-deep-orange-500 dark:tw-bg-black">
      <div class="tw-flex tw-h-10 tw-items-center tw-justify-between tw-border-b tw-p-1">
        <button
          type="button"
          data-testid="studio-calendar-arrow-left-button"
          class="tw-h-full tw-rounded tw-px-4 hover:tw-bg-deep-orange-50"
          @click="setPreviousMonth"
        >
          <TablerArrowLeft class="tw-h-5 tw-w-5" />
        </button>
        <div data-testid="studio-calendar-month-year-text" class="tw-text-sm dark:tw-text-white">
          {{ `${MONTHS[visibleMonthDate.getMonth()]} ${visibleMonthDate.getFullYear()}` }}
        </div>
        <button
          type="button"
          data-testid="studio-calendar-arrow-right-button"
          class="tw-h-full tw-rounded tw-px-4 hover:tw-bg-deep-orange-50"
          @click="setNextMonth"
        >
          <TablerArrowRight class="tw-h-5 tw-w-5" />
        </button>
      </div>
      <div class="tw-grid tw-grid-cols-7 tw-justify-center tw-pt-4 tw-text-center">
        <div v-for="day in DAYS" :key="day" class="tw-text-xs tw-font-medium tw-text-deep-orange-500">
          {{ day }}
        </div>
      </div>
      <div class="tw-grid tw-grid-cols-7 tw-grid-rows-5 tw-pb-3 tw-text-center">
        <div
          v-for="date in datesOfMonth"
          :key="date.valueOf()"
          class="tw-flex tw-h-10 tw-items-center tw-justify-center"
          :style="{ gridColumnStart: date.getDay() + 1 }"
        >
          <button
            type="button"
            data-testid="studio-calendar-date-button"
            :class="{
              'tw-flex tw-h-7 tw-w-7 tw-flex-col tw-items-center tw-justify-center tw-rounded-full hover:tw-ring hover:tw-ring-deep-orange-500': true,
              'tw-bg-deep-orange-500': isDateSelected(date),
              'tw-ring tw-ring-slate-100 tw-ring-offset-2 tw-ring-offset-white dark:tw-ring-slate-800 dark:tw-ring-offset-black':
                isDateToday(date),
              'tw-cursor-not-allowed hover:tw-ring-0': isDateDisabled(date)
            }"
            :disabled="isDateDisabled(date)"
            @click="selectDate(date)"
          >
            <div
              :class="{
                'tw-text-sm tw-text-black dark:tw-text-white': true,
                'tw-text-slate-400 dark:tw-text-slate-400': isDateDisabled(date),
                'tw-text-white': isDateSelected(date)
              }"
            >
              {{ date.getDate() }}
            </div>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';

import {
  DAYS,
  MONTHS,
  getDatesOfMonth,
  isDateToday,
  isDateBeforeToday,
  areDatesEqual,
  isDateBetween,
  addDaysToDate,
  addMonthsToDate,
  getMidDateOfCurrentMonth
} from '../utils/date';
import { getTimezoneOffsetOnGivenDateTime } from '../utils/timezone';
import TablerArrowLeft from './Icons/tabler/TablerArrowLeft.vue';
import TablerArrowRight from './Icons/tabler/TablerArrowRight.vue';

export default defineComponent({
  name: 'StudioCalendar',
  components: {
    TablerArrowRight,
    TablerArrowLeft
  },
  props: {
    initialDate: {
      type: Date as PropType<Date | null>,
      default: undefined
    },
    durationDays: {
      type: Number,
      default: 1
    },
    deselectionEnabled: {
      type: Boolean,
      default: false
    },
    disableDatesBeforeToday: {
      type: Boolean,
      default: true
    },
    // For pickers where no valid time is left today.
    disableToday: {
      type: Boolean,
      default: false
    },
    label: {
      type: String,
      default: ''
    },
    timezone: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      /**
       * Date selected by the user
       */
      selectedDate: this.initialDate as Date | null,
      /**
       * For the month which is currently visible in the calendar
       */
      visibleMonthDate: (this.initialDate || getMidDateOfCurrentMonth()) as Date,
      DAYS: DAYS as string[],
      MONTHS: MONTHS as string[]
    };
  },
  computed: {
    datesOfMonth(): Date[] {
      return getDatesOfMonth(this.visibleMonthDate);
    }
  },
  watch: {
    initialDate(newDate: Date | null) {
      this.selectedDate = newDate;
      this.visibleMonthDate = newDate || getMidDateOfCurrentMonth();
    }
  },
  methods: {
    setNextMonth() {
      this.visibleMonthDate = addMonthsToDate(this.visibleMonthDate, 1);
    },
    setPreviousMonth() {
      this.visibleMonthDate = addMonthsToDate(this.visibleMonthDate, -1);
    },
    selectDate(date: Date) {
      if (this.deselectionEnabled && this.selectedDate && areDatesEqual(date, this.selectedDate)) {
        this.selectedDate = null;
      } else {
        this.selectedDate = date;
      }

      this.$emit('selectDate', this.selectedDate);
    },
    isDateToday(date: Date) {
      return isDateToday(date, getTimezoneOffsetOnGivenDateTime(date, this.timezone));
    },
    isDateDisabled(date: Date) {
      if (this.disableToday && this.isDateToday(date)) {
        return true;
      }

      if (!this.disableDatesBeforeToday) {
        return false;
      }

      if (isDateBeforeToday(date, getTimezoneOffsetOnGivenDateTime(date, this.timezone))) {
        return true;
      }

      return false;
    },
    isDateSelected(date: Date) {
      return (
        this.selectedDate &&
        (areDatesEqual(date, this.selectedDate) ||
          isDateBetween(date, this.selectedDate, addDaysToDate(this.selectedDate, this.durationDays - 1)))
      );
    }
  }
});
</script>
